import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Mail, Lock, User as UserIcon, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from '@/components/ui/sonner';
import {
  getInviteContext,
} from '@/lib/inviteContext';

interface AuthModalProps {
  open: boolean;
  mode: 'signin' | 'signup';
  onOpenChange: (open: boolean) => void;
  onSwitchMode: (mode: 'signin' | 'signup') => void;
}

// Magic-link sign-in has been removed from the UI per product spec.
// Only email/password and forgot-password flows are exposed now.
type View = 'auth' | 'forgot';

const MIN_PASSWORD_LENGTH = 6;

/**
 * Map raw Supabase auth errors to friendly, user-facing copy.
 * Keeps the original message as a fallback so we never strip useful detail.
 */
const friendlyAuthError = (raw: string, mode: 'signin' | 'signup'): string => {
  const lower = raw.toLowerCase();

  if (lower.includes('invalid login') || lower.includes('invalid credentials')) {
    return 'Incorrect email or password. Please try again.';
  }
  if (lower.includes('email not confirmed')) {
    return 'Please confirm your email address. Check your inbox for the confirmation link.';
  }
  if (lower.includes('user already registered') || lower.includes('already registered') || lower.includes('already exists')) {
    return 'An account with this email already exists. Try signing in instead.';
  }
  if (lower.includes('password') && lower.includes('weak')) {
    return `Password is too weak. Use at least ${MIN_PASSWORD_LENGTH} characters with a mix of letters and numbers.`;
  }
  if (lower.includes('password') && lower.includes('short')) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'Too many attempts. Please wait a moment and try again.';
  }
  if (lower.includes('network') || lower.includes('fetch')) {
    return 'Network error. Check your connection and try again.';
  }
  if (mode === 'signin' && lower.includes('user not found')) {
    return "We couldn't find an account with that email. Try signing up instead.";
  }
  return raw || 'Something went wrong. Please try again.';
};

const AuthModal: React.FC<AuthModalProps> = ({ open, mode, onOpenChange, onSwitchMode }) => {
  const { signIn, signUp, sendPasswordReset, resendConfirmationEmail, isPaid, isStaff } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [view, setView] = useState<View>('auth');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  // True when the most recent sign-in failure was email_not_confirmed.
  // Surfaces a "Resend confirmation email" CTA right under the error
  // banner so the user can recover without leaving the modal.
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resending, setResending] = useState(false);

  // Reset transient state shortly after the modal closes so the next open
  // shows a clean form (but we don't yank fields out from under a closing
  // animation).
  useEffect(() => {
    if (!open) {
      const t = setTimeout(() => {
        setView('auth');
        setName('');
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setError(null);
        setInfo(null);
        setLoading(false);
        setNeedsConfirmation(false);
        setResending(false);
      }, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  // When the parent toggles between sign-in / sign-up, drop transient state
  // (errors, alt views) but keep what the user typed so they don't lose it.
  useEffect(() => {
    setView('auth');
    setError(null);
    setInfo(null);
    setNeedsConfirmation(false);
    setConfirmPassword('');
  }, [mode]);

  // Live validation hint for the confirm-password field on the signup form.
  const passwordsMismatch = useMemo(
    () =>
      mode === 'signup' &&
      confirmPassword.length > 0 &&
      password !== confirmPassword,
    [mode, password, confirmPassword]
  );

  const passwordTooShort = useMemo(
    () => mode === 'signup' && password.length > 0 && password.length < MIN_PASSWORD_LENGTH,
    [mode, password]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Common required-field guard
    if (!email.trim() || !password.trim()) {
      setError('Email and password are required.');
      return;
    }

    if (mode === 'signup') {
      if (!name.trim()) {
        setError('Please enter your full name.');
        return;
      }
      if (password.length < MIN_PASSWORD_LENGTH) {
        setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
        return;
      }
      if (password !== confirmPassword) {
        setError("Passwords don't match. Please re-enter to confirm.");
        return;
      }
    }

    setError(null);
    setInfo(null);
    setLoading(true);

    try {
      const result =
        mode === 'signin'
          ? await signIn(email, password)
          : await signUp(email, password, { name });
      if (!result.ok) {
        const friendly = friendlyAuthError(result.error, mode);
        setError(friendly);
        // Detect "email_not_confirmed" so we can show a one-click
        // resend-confirmation CTA inside the error banner. Match against
        // BOTH the raw error (which is what Supabase returns) and the
        // friendly copy as a defensive double-check.
        const rawLower = (result.error || '').toLowerCase();
        const friendlyLower = friendly.toLowerCase();
        const unconfirmed =
          mode === 'signin' &&
          (rawLower.includes('email_not_confirmed') ||
            rawLower.includes('email not confirmed') ||
            friendlyLower.includes('confirm your email'));
        setNeedsConfirmation(unconfirmed);
        setLoading(false);
        return;
      }

      onOpenChange(false);

      const pendingInvite =
        getInviteContext();

      if (
        pendingInvite?.acceptanceMode
      ) {
        toast.success(
          mode === 'signin'
            ? 'Welcome back — finish joining your family.'
            : 'Account created — finish joining your family.',
        );

        navigate('/app?invited=1');

        if (
          pendingInvite.acceptanceMode ===
          'SELF_PAID'
        ) {
          window.setTimeout(() => {
            try {
              window.dispatchEvent(
                new CustomEvent(
                  'kc:open-trial-checkout',
                ),
              );
            } catch {
              /* noop */
            }
          }, 700);
        }

        return;
      }

      if (mode === 'signin') {
        console.log('User logged in successfully');
        toast.success('Welcome back!');
        // Post-login redirect:
        //  - Subscribed (paid plan) or staff (owner/admin) → land on the
        //    family-tree dashboard at "/".
        //  - Authenticated but NOT yet subscribed → still land on "/" and
        //    pop the existing trial-checkout modal so the user lands on
        //    the trial/pricing surface directly.
        // We skip redirect when the user is already on the home page (or
        // a logged-in surface like /account) so we don't interrupt them.
        const path = location.pathname || '';
        const stayPaths = ['/app', '/settings', '/settings/email'];
        const shouldNavigate = !stayPaths.includes(path);
        if (shouldNavigate) {
          navigate('/app');
        }
        if (!isPaid && !isStaff) {
          // Defer slightly so the modal close animation can finish before
          // the trial-checkout modal pops.
          window.setTimeout(() => {
            try {
              window.dispatchEvent(new CustomEvent('kc:open-trial-checkout'));
            } catch {
              /* noop */
            }
          }, 250);
        }
      } else {
        toast.success('Account created — let\'s start your free trial.');
        // After signup, immediately route into the trial-checkout flow.
        // Card is required to start the 3-day free trial; user is not
        // charged until the trial ends.
        const path = location.pathname || '';
        const isOnboardingPath =
          path.startsWith('/join') ||
          path.startsWith('/invite') ||
          path.startsWith('/settings');
        if (!isOnboardingPath) {
          navigate('/app');
        }
        // Open the membership/trial modal so checkout starts right away.
        // Defer slightly so the auth modal close animation can finish.
        window.setTimeout(() => {
          try {
            window.dispatchEvent(new CustomEvent('kc:open-trial-checkout'));
          } catch {
            /* noop */
          }
        }, 250);
      }

    } catch (err: any) {
      setError(friendlyAuthError(err?.message || 'Something went wrong.', mode));
    } finally {
      setLoading(false);
    }
  };

  // Re-send the email-confirmation link for the email currently typed in
  // the auth form. Surfaced as a CTA inside the red error banner whenever
  // sign-in fails with "email_not_confirmed". Auth/Resend pipelines are
  // unchanged — this just calls the existing supabase.auth.resend wrapper.
  const handleResendConfirmation = async () => {
    if (!email.trim()) {
      setError('Please enter your email address above first.');
      return;
    }
    setResending(true);
    setInfo(null);
    try {
      const result = await resendConfirmationEmail(email);
      if (!result.ok) {
        setError(friendlyAuthError(result.error, 'signin'));
        return;
      }
      // Keep the red banner cleared and surface a calm green confirmation.
      setError(null);
      setNeedsConfirmation(false);
      setInfo(`Confirmation email re-sent to ${email}. Check your inbox (and spam folder).`);
      toast.success('Confirmation email sent');
    } catch (err: any) {
      setError(friendlyAuthError(err?.message || 'Failed to resend confirmation email.', 'signin'));
    } finally {
      setResending(false);
    }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!email.trim()) {
      setError('Please enter your email address.');
      return;
    }
    setError(null);
    setInfo(null);
    setLoading(true);
    const result = await sendPasswordReset(email);
    setLoading(false);
    if (!result.ok) {
      setError(friendlyAuthError(result.error, 'signin'));
      return;
    }
    setInfo(`Password reset email sent to ${email}. Check your inbox.`);
    toast.success('Password reset email sent');
  };

  const headerTitle =
    view === 'forgot'
      ? 'Reset your password'
      : mode === 'signin'
        ? 'Welcome back'
        : 'Create your account';

  const headerSubtitle =
    view === 'forgot'
      ? "Enter your email and we'll send you a reset link."
      : mode === 'signin'
        ? 'Sign in to your KinsCircle family network.'
        : 'Start your private family network in seconds.';


  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-0 overflow-hidden bg-white">
        <div className="bg-gradient-to-br from-fuchsia-700 via-fuchsia-600 to-pink-600 text-white p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center ring-2 ring-white/30">
              <img
                src="/brand/kinscircle-tree-icon.png"
                alt="KinsCircle"
                className="w-full h-full rounded-full object-cover"
              />
            </div>
            <div>
              <DialogTitle className="font-serif text-2xl font-bold leading-tight">
                {headerTitle}
              </DialogTitle>
              <DialogDescription className="text-white/80 text-xs mt-0.5">
                {headerSubtitle}
              </DialogDescription>
            </div>
          </div>
        </div>

        {/* Forgot password view */}
        {view === 'forgot' && (
          <form onSubmit={handleForgot} className="p-6 space-y-4">
            <div>
              <Label htmlFor="forgot-email" className="text-slate-800">Email</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="forgot-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@family.com"
                  className="pl-9"
                  autoComplete="email"
                />
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3">
                {error}
              </div>
            )}
            {info && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-3">
                {info}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                </>
              ) : (
                'Send Reset Link'
              )}
            </Button>

            <button
              type="button"
              onClick={() => { setView('auth'); setError(null); setInfo(null); }}
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-fuchsia-700 mx-auto"
            >
              <ArrowLeft className="w-4 h-4" /> Back to sign in
            </button>
          </form>
        )}

        {/* Default sign-in / sign-up view (email + password only) */}
        {view === 'auth' && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" noValidate>
            {mode === 'signup' && (
              <div>
                <Label htmlFor="auth-name" className="text-slate-800">Full Name</Label>
                <div className="relative mt-1">
                  <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="auth-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Jane Walker"
                    className="pl-9"
                    autoComplete="name"
                    required
                  />
                </div>
              </div>
            )}

            <div>
              <Label htmlFor="auth-email" className="text-slate-800">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="auth-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@family.com"
                  className="pl-9"
                  autoComplete="email"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="auth-password" className="text-slate-800">Password</Label>
                {mode === 'signin' && (
                  <button
                    type="button"
                    onClick={() => { setView('forgot'); setError(null); setInfo(null); }}
                    className="text-xs font-semibold text-fuchsia-700 hover:underline"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="auth-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={mode === 'signup' ? `At least ${MIN_PASSWORD_LENGTH} characters` : 'Your password'}
                  className="pl-9"
                  autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                  minLength={MIN_PASSWORD_LENGTH}
                />
              </div>
              {passwordTooShort && (
                <p className="text-xs text-amber-700 mt-1">
                  Password must be at least {MIN_PASSWORD_LENGTH} characters.
                </p>
              )}
            </div>

            {mode === 'signup' && (
              <div>
                <Label htmlFor="auth-confirm" className="text-slate-800">Confirm Password</Label>
                <div className="relative mt-1">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="auth-confirm"
                    type="password"
                    required
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className="pl-9"
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD_LENGTH}
                    aria-invalid={passwordsMismatch}
                  />
                </div>
                {passwordsMismatch ? (
                  <p className="text-xs text-red-600 mt-1">Passwords don't match.</p>
                ) : confirmPassword.length > 0 && password === confirmPassword ? (
                  <p className="text-xs text-emerald-700 mt-1 flex items-center gap-1">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Passwords match.
                  </p>
                ) : null}
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg p-3 space-y-2">
                <div>{error}</div>
                {needsConfirmation && (
                  <button
                    type="button"
                    onClick={handleResendConfirmation}
                    disabled={resending}
                    className="inline-flex items-center gap-1.5 rounded-md bg-red-100 hover:bg-red-200 disabled:opacity-60 px-3 py-1.5 text-xs font-semibold text-red-900 transition"
                  >
                    {resending ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Sending…</>
                    ) : (
                      <><Mail className="w-3.5 h-3.5" /> Resend confirmation email</>
                    )}
                  </button>
                )}
              </div>
            )}


            {info && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded-lg p-3">
                {info}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Please wait…
                </>
              ) : mode === 'signin' ? (
                'Sign In'
              ) : (
                'Create Account'
              )}
            </Button>

            <div className="text-center text-sm text-slate-600">
              {mode === 'signin' ? (
                <>
                  Don't have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setError(null); setInfo(null); onSwitchMode('signup'); }}
                    className="text-fuchsia-700 font-semibold hover:underline"
                  >
                    Sign up
                  </button>
                </>
              ) : (
                <>
                  Already have an account?{' '}
                  <button
                    type="button"
                    onClick={() => { setError(null); setInfo(null); onSwitchMode('signin'); }}
                    className="text-fuchsia-700 font-semibold hover:underline"
                  >
                    Sign in
                  </button>
                </>
              )}
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
